import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import i18n from '../../../i18n';

export interface ApiBlogPost {
    id: string;
    slug: string;
    coverImage?: string;
    title: string;
    excerpt?: string;
    content?: string;
    metaTitle?: string;
    metaDescription?: string;
    author?: { id: string; name: string; avatar?: string };
    category?: { id: string; slug: string; name: string } | null;
    published: boolean;
    publishedAt?: string;
    readTime?: number;
}

export interface ApiBlogCategory {
    id: string;
    slug: string;
    name: string;
    postsCount: number;
}

export interface BlogListResponse {
    data: ApiBlogPost[];
    pagination: { page: number; limit: number; total: number; pages: number };
}

// Estimate read time (words per minute = 200)
export function estimateReadTime(content: string): number {
    if (!content) return 1;
    const plainText = content.replace(/<[^>]+>/g, ' ');
    const wordCount = plainText.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(wordCount / 200));
}

export function usePublicBlogPosts(params?: { page?: number; limit?: number; categoryId?: string }) {
    const locale = i18n.language || 'en';
    const { page = 1, limit = 9, categoryId } = params || {};

    return useQuery<BlogListResponse>({
        queryKey: ['blog', 'public', locale, page, limit, categoryId],
        queryFn: async () => {
            const searchParams = new URLSearchParams({
                page: String(page),
                limit: String(limit),
                locale,
                ...(categoryId ? { categoryId } : {}),
            });
            const res = await api.get<BlogListResponse>(`/api/blog?${searchParams}`);
            return res as unknown as BlogListResponse;
        },
        staleTime: 5 * 60 * 1000,
    });
}

export function useBlogPost(slug: string) {
    const locale = i18n.language || 'en';

    return useQuery<ApiBlogPost | null>({
        queryKey: ['blog', 'post', slug, locale],
        queryFn: async () => {
            try {
                const res = await api.get<any>(`/api/blog/${slug}?locale=${locale}`);
                return (res as any).data ?? (res as any) ?? null;
            } catch {
                return null;
            }
        },
        enabled: !!slug,
        staleTime: 5 * 60 * 1000,
    });
}

export function useBlogCategories() {
    const locale = i18n.language || 'en';

    return useQuery<ApiBlogCategory[]>({
        queryKey: ['blog', 'categories', locale],
        queryFn: async () => {
            const res = await api.get<any>(`/api/blog/categories?locale=${locale}`);
            return (res as any).data ?? [];
        },
        staleTime: 10 * 60 * 1000,
    });
}
